import { useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Placeholder from '../../components/Placeholder';
import { useAuth } from '../../auth/AuthContext';
import { apiGet, apiPost, errOf } from '../../lib/api';
import { reasonKeyOf, uploadMemberPhoto } from '../../lib/photo';

const inputStyle: CSSProperties = {
  width: '100%',
  borderRadius: 8,
  padding: '8px 12px',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
};

/** 設定（tab 5）—— 本人資訊 ＋ 設定／更改密碼（跟 CoEldery 85：電話號碼＋密碼登入）＋ 管理台 ＋ 登出 */
export default function SettingsPage() {
  const { t } = useTranslation();
  const { user, logout, refresh } = useAuth();
  const isStaff = user?.role === 'ai85' || user?.role === 'staff';
  const hasPw = user?.has_password === true;

  const [cur, setCur] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  /* ── 我的相片（`decisions/012`：會員自己設定；上 Cloudinary 之後存 URL）── */
  const [photo, setPhoto] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoMsg, setPhotoMsg] = useState('');
  const [photoErr, setPhotoErr] = useState('');

  useEffect(() => {
    void apiGet<{ card: { member: { photo_url?: string | null } } | null }>('/api/me/card').then((r) => {
      if (r.ok && r.data?.card) setPhoto(r.data.card.member.photo_url ?? null);
    });
  }, []);

  /** 存相片（`url`）或移除（`null`） */
  async function savePhoto(url: string | null) {
    setPhotoErr('');
    setPhotoMsg('');
    const r = await apiPost('/api/me/photo', { photo_url: url });
    if (r.ok) {
      setPhoto(url);
      setPhotoMsg(t('photo_saved'));
    } else {
      setPhotoErr(errOf(r.data) || t('photo_err_generic'));
    }
  }

  /** 揀圖 → 縮圖 → 上 Cloudinary → 存 URL */
  async function onPickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPhotoErr('');
    setPhotoMsg('');
    setPhotoBusy(true);
    const up = await uploadMemberPhoto(file);
    if (!up.ok) {
      setPhotoBusy(false);
      setPhotoErr(t(reasonKeyOf(up.reason)));
      return;
    }
    await savePhoto(up.url);
    setPhotoBusy(false);
  }

  async function savePw(e: FormEvent) {
    e.preventDefault();
    if (pw1 !== pw2) {
      setErr(t('setpw_mismatch'));
      return;
    }
    setBusy(true);
    setErr('');
    setMsg('');
    const r = await apiPost('/api/auth/change-password', { currentPassword: cur, newPassword: pw1 });
    setBusy(false);
    if (r.ok) {
      setMsg(t('pw_saved'));
      setCur('');
      setPw1('');
      setPw2('');
      await refresh();
    } else {
      setErr(errOf(r.data) || t('pw_failed'));
    }
  }

  return (
    <Placeholder title={t('tab.settings')}>
      <p className="mb-1">{t('member_greeting', { email: user?.email ?? '' })}</p>
      <p className="mb-4">{t('member_role', { role: user?.role ?? '' })}</p>

      {isStaff ? (
        <p className="mb-4">
          <Link to="/admin" className="font-semibold" style={{ color: 'var(--color-primary)' }}>
            → {t('admin_title')}
          </Link>
        </p>
      ) : null}

      {!isStaff ? (
        <section className="mb-6 text-left">
          <h2 className="font-semibold mb-1">{t('photo_title')}</h2>
          <div className="flex items-center gap-3 mt-1">
            {photo ? (
              <img
                src={photo}
                alt=""
                style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <span
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: 'var(--color-bg-tint)',
                  display: 'inline-block',
                  flex: '0 0 auto',
                }}
              />
            )}
            <input
              type="file"
              accept="image/*"
              disabled={photoBusy}
              onChange={onPickPhoto}
              style={{ fontSize: 'var(--font-size-small)' }}
            />
            {photo ? (
              <button
                type="button"
                disabled={photoBusy}
                onClick={() => void savePhoto(null)}
                className="rounded-lg px-3 py-2"
                style={{ background: 'var(--color-primary-soft)', color: 'var(--color-primary-strong)' }}
              >
                {t('f_photo_remove')}
              </button>
            ) : null}
          </div>
          {photoBusy ? (
            <p className="text-sm mt-2" style={{ color: 'var(--color-text-secondary)' }}>
              {t('photo_uploading')}
            </p>
          ) : null}
          {photoErr ? (
            <p className="text-sm mt-2" style={{ color: 'var(--color-danger)' }}>
              {photoErr}
            </p>
          ) : null}
          {photoMsg ? (
            <p className="text-sm mt-2" style={{ color: 'var(--color-primary)' }}>
              {photoMsg}
            </p>
          ) : null}
        </section>
      ) : null}

      {hasPw ? (
        <section className="mb-6 text-left">
          <h2 className="font-semibold mb-1">{t('pw_title')}</h2>
          <form onSubmit={savePw}>
            <input
              type="password"
              autoComplete="current-password"
              placeholder={t('pw_current')}
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              className="mb-2"
              style={inputStyle}
            />
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              placeholder={t('setpw_new')}
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              className="mb-2"
              style={inputStyle}
            />
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              placeholder={t('setpw_confirm')}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="mb-2"
              style={inputStyle}
            />
            {err ? (
              <p className="text-sm mb-2" style={{ color: 'var(--color-danger)' }}>
                {err}
              </p>
            ) : null}
            {msg ? (
              <p className="text-sm mb-2" style={{ color: 'var(--color-primary)' }}>
                {msg}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={busy}
              className="rounded-lg px-4 py-2 font-semibold"
              style={{ background: 'var(--color-primary)', color: 'var(--color-on-primary)', opacity: busy ? 0.6 : 1 }}
            >
              {busy ? t('setpw_working') : t('pw_save')}
            </button>
          </form>
        </section>
      ) : null}

      <button
        type="button"
        onClick={() => void logout()}
        className="rounded-lg px-4 py-2 font-semibold"
        style={{ background: 'var(--color-primary-soft)', color: 'var(--color-primary-strong)' }}
      >
        {t('logout')}
      </button>
    </Placeholder>
  );
}

